import { useMemoizedFn } from "ahooks";
import { MutableRefObject, useEffect, useState } from "react";

export default function useOnceInViewport(
  ref: MutableRefObject<Element>,
  defaultState: boolean = false
) {
  const [state, setState] = useState(defaultState);

  useEffect(() => {
    // 检查 ref.current 是否存在，避免在元素未挂载时调用 observe
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const isIntersecting = entry.isIntersecting;
        if (isIntersecting) {
          observer.disconnect();
        }
        setState(isIntersecting);
      }
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return state;
}

export function useInViewport(
  getTarget: (() => HTMLElement) | MutableRefObject<Element>,
  option: IntersectionObserverInit = {}
) {
  const [isViewport, setIsViewport] = useState(false);

  const getTargetFn = useMemoizedFn(() => {
    if (getTarget && typeof getTarget === "function") {
      return getTarget();
    }

    const targetRef = getTarget as MutableRefObject<Element>;

    if (targetRef?.current) {
      return targetRef.current;
    }
  });

  useEffect(() => {
    const target = getTargetFn();

    // 检查 target 是否存在，避免在元素未挂载时调用 observe
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const isIntersecting = entry.isIntersecting;
        setIsViewport(isIntersecting);
      }
    }, option || {});

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [getTargetFn, option]);

  return [isViewport];
}
